import { CreativeOSPages } from '@/_shared/subscriptionPlans'
import AccessControl from '@/components/AccessControl'
import InspirationAnalysisDetails from '@/components/InspirationAnalysisDetails'
import { OrderFilterSelect } from '@/components/ad-inspiration/OrderFilterSelect'
import { Stack } from '@/components/custom-components'
import { CreativeAnalysisTabs } from '@/components/custom-components/CreativeAnalysisTabs'
import { Loader } from '@/components/custom-components/Loader'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/shadcn/breadcrumb.tsx'
import { Button } from '@/components/shadcn/button.tsx'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import useCardLayoutWithImpressions from '@/hooks/useCardLayoutWithImpressions'
import { featureFlagKeys } from '@/utils/data/featureFlags'
import { trpc } from '@/utils/trpc'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { LoaderIcon } from 'lucide-react'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'

type InspirationLibraryOrderFilter = 'Random' | 'Recent' | 'Oldest' | 'Highest Score' | 'Lowest Score'

type SearchParams = {
  sideBarOpen?: boolean
  orderFilter?: InspirationLibraryOrderFilter
}

export const Route = createFileRoute('/feeds/creative-toolkit/funnel-creative-analyzer/inspiration-library/')({
  component: All,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    // const industry = search?.industry as string | undefined
    const sideBarOpen = search?.sideBarOpen as boolean
    const orderFilter = search?.orderFilter as InspirationLibraryOrderFilter | undefined

    return {
      // industry,
      sideBarOpen,
      orderFilter,
    }
  },
})

function All() {
  const [allData, setAllData] = useState<InspirationItemProps[] | undefined>(undefined)
  const inspirationLibraryFlagEnabled = useFeatureFlagEnabled(featureFlagKeys.funnelInspiration)
  const fcaHistoryFlagEnabled = useFeatureFlagEnabled(featureFlagKeys.fcaHistory)
  const navigate = useNavigate()
  const fcaTabsItems = useMemo(() => {
    const items = [
      {
        name: 'Upload Assets',
        link: '/feeds/creative-toolkit/funnel-creative-analyzer',
      },
    ]
    if (inspirationLibraryFlagEnabled) {
      items.push({
        name: 'Inspirations',
        link: '/feeds/creative-toolkit/funnel-creative-analyzer/inspiration-library',
      })
    }
    if (fcaHistoryFlagEnabled) {
      items.push({
        name: 'My Analyses',
        link: '/feeds/creative-toolkit/funnel-creative-analyzer/history',
      })
    }
    return items
  }, [inspirationLibraryFlagEnabled, fcaHistoryFlagEnabled])

  const { orderFilter } = Route.useSearch()
  const [cursor, setCursor] = useState(1)

  // update cursor whenever selectedFilters change
  useEffect(() => {
    setCursor(1) // Reset cursor to 1 whenever filters change
  }, [orderFilter])

  useEffect(() => {
    if (inspirationLibraryFlagEnabled === false) {
      navigate({ to: '/feeds/creative-toolkit' })
      return
    }
  }, [inspirationLibraryFlagEnabled])

  const {
    data: funnelFeedInspiration,
    isLoading: isLoadingFunnelFeedInspiration,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = trpc.funnelFeed.useInfiniteQuery(
    {
      limit: 10,
      order: orderFilter,
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      initialCursor: cursor,
    },
  )

  const { ref: scrollRef, inView } = useInView({
    threshold: 0,
    trackVisibility: true,
    delay: 100,
    initialInView: false,
  })

  useEffect(() => {
    if (
      inView &&
      hasNextPage &&
      !isFetchingNextPage &&
      funnelFeedInspiration &&
      allData &&
      allData.length &&
      funnelFeedInspiration.pages[funnelFeedInspiration.pages.length - 1]?.results.length > 0
    ) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, fetchNextPage, funnelFeedInspiration, isFetchingNextPage, allData])

  useEffect(() => {
    if (!funnelFeedInspiration) return

    const uniqueResults = new Map<string, InspirationItemProps>()
    funnelFeedInspiration.pages.forEach((page) => {
      page.results.forEach((result) => uniqueResults.set(result.id, result))
    })

    setAllData((prevData) => {
      const newData = Array.from(uniqueResults.values())
      if (JSON.stringify(prevData) === JSON.stringify(newData)) return prevData // Prevent redundant updates
      return newData
    })
  }, [funnelFeedInspiration])

  const { squareRef } = useCardLayoutWithImpressions()

  return (
    <>
      <AccessControl page={CreativeOSPages.CREATIVE_TOOLKIT_COPY_GRADER}>
        <Stack ref={squareRef} className="gap-3 lg:gap-6">
          <Stack className="gap-3 lg:gap-8">
            <Stack className="gap-3 lg:gap-6">
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to={'/feeds/creative-toolkit'}>Toolkit</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>Creative Analysis</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
              <div className={'flex justify-between gap-5 items-center'}>
                <div className={''}>
                  <h1 className={'text-lg lg:text-2xl font-semibold'}>Inspiration Library</h1>
                  <p className={'text-lg lg:text-base text-thememutedforeground mt-2'}>
                    Browse high-performing ad and landing page pairs from CreativeOS Inspiration Library.
                  </p>
                </div>
              </div>
              <div className="flex justify-between gap-3">
                <CreativeAnalysisTabs tabItems={fcaTabsItems} />
                <div className="flex justify-center items-center gap-2">
                  <div>
                    <OrderFilterSelect
                      defaultFilter={orderFilter || 'Highest Score'}
                      options={['Random', 'Recent', 'Oldest', 'Highest Score', 'Lowest Score']}
                    />
                  </div>
                </div>
              </div>
            </Stack>
          </Stack>
          <div></div>
          <div>
            {isLoadingFunnelFeedInspiration ? (
              <div className="flex flex-col justify-center items-center w-full h-[60vh] min-h-[60vh] max-h-[60vh]">
                <Loader />
              </div>
            ) : allData && allData.length > 0 ? (
              <div ref={squareRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allData.map((item) => (
                  <InspirationItem data={item} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center w-full h-[60vh] min-h-[60vh] max-h-[60vh]">
                <p className="text-sm text-thememutedforeground">No inspirations found</p>
              </div>
            )}
            <div ref={scrollRef} className="flex justify-center w-full min-h-14 h-14">
              {isFetchingNextPage && <Loader />}
            </div>
          </div>
        </Stack>
      </AccessControl>
    </>
  )
}

type InspirationItemProps = {
  id: string
  brandName: string | null
  brandImage: string | null
  brandId: string | null
  adImageUrl: string | null
  landingPageMobileScreenshotUrl: string | null
  landingPageDesktopScreenshotUrl: string | null
  grade?: string | null
  overallScore: number | null
}

export const InspirationItem = ({ data }: { data: InspirationItemProps }) => {
  const viewFCAAnalysisFlagEnabled = useFeatureFlagEnabled(featureFlagKeys.viewFCAAnalysis)

  const [analysisOpen, setAnalysisOpen] = useState<boolean>(false)

  const { data: analysisData, isLoading: isLoadingAnalysisData } = trpc.funnelAnalysis.useQuery(
    {
      id: data.id,
    },
    {
      enabled: analysisOpen,
    },
  )

  return (
    <div className="p-6 border rounded-xl space-y-[1.625rem]">
      {analysisOpen && analysisData && !isLoadingAnalysisData && (
        <InspirationAnalysisDetails
          data={{
            ad_images_data: data.adImageUrl ?? '',
            landing_page_image_data: analysisData.response.landing_page_image_data?.image_url ?? '',
            analysis: {
              analysis: {
                overview: analysisData.response.analysis.analysis.overview || '',
                whatsWorkingWell: analysisData.response.analysis.analysis.whatsWorkingWell || '',
                areasForImprovement: analysisData.response.analysis.analysis.areasForImprovement || '',
              },
              opportunity_assessment: {
                score: analysisData.response.analysis.opportunity_assessment?.score || 0,
                key_opportunities:
                  analysisData.response.analysis.opportunity_assessment?.key_opportunities?.map((opportunity) => ({
                    heading: opportunity.heading || '',
                    description: opportunity.description || '',
                  })) || [],
              },
              coherence_evaluation: {
                ...analysisData.response.analysis.coherence_evaluation,
                score_breakdown: Object.entries(
                  analysisData.response.analysis.coherence_evaluation.score_breakdown || {},
                ).reduce(
                  (acc, [key, value]) => ({
                    ...acc,
                    [key]: {
                      score: value.score || 0,
                      description: value.description || '',
                    },
                  }),
                  {},
                ),
                overall_score: {
                  ...analysisData.response.analysis.coherence_evaluation.overall_score,
                  emotional_label:
                    analysisData.response.analysis.coherence_evaluation.overall_score.emotional_label || '',
                  visceral_description:
                    analysisData.response.analysis.coherence_evaluation.overall_score.visceral_description || '',
                },
              },
            },
          }}
          open={analysisOpen}
          onOpenChange={() => setAnalysisOpen(false)}
          showUseThisFormatButton={false}
        />
      )}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h5 className="font-semibold">{data.brandName}</h5>
        </div>
        <div>
          <Tooltip>
            <TooltipTrigger>
              <div className="bg-gradient-to-r from-[#C31DCD] via-[#FB5D58] to-yellow-500 flex justify-center items-center gap-2 h-8 p-[1.6px] rounded-lg">
                <div className="flex justify-center items-center bg-purple-50 rounded-md w-full h-full px-3.5">
                  <span className="font-bold bg-gradient-to-r from-[#C31DCD] via-[#FB5D58] to-yellow-500 bg-clip-text text-transparent">
                    {data.grade == 'A' ? '🔥' : null}
                    {data.grade}
                  </span>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                Score: <span>{data.overallScore}</span>
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="relative bg-gray-200 rounded-lg h-[13.625rem]">
          <div className="absolute top-2 left-2 font-medium text-xs rounded-full bg-secondary px-2 py-0.5">
            <span>Ad</span>
          </div>
          <img src={data.adImageUrl ?? ''} alt="" className="h-[13.625rem] object-cover w-full rounded-lg" />
        </div>
        <div className="relative bg-gray-200 rounded-lg h-[13.625rem]">
          <div className="absolute top-2 left-2 font-medium text-xs rounded-full bg-secondary px-2 py-0.5">
            <span>Landing Page</span>
          </div>
          <img
            src={data.landingPageMobileScreenshotUrl ?? ''}
            alt=""
            className="h-[13.625rem] object-cover w-full rounded-lg"
          />
        </div>
      </div>
      {viewFCAAnalysisFlagEnabled ? (
        <div className="flex justify-center">
          <Button
            disabled={isLoadingAnalysisData}
            onClick={() => setAnalysisOpen(true)}
            variant={'outline'}
            size={'sm'}
          >
            {isLoadingAnalysisData ? <LoaderIcon className="animate-spin" /> : 'View Analysis'}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
